import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Clock, CheckCircle, AlertCircle, Calendar, RefreshCw, Undo2 } from 'lucide-react';
import { remainderAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { format, parseISO, isToday, isPast } from 'date-fns';

const Remainder = () => {
  const { canAdd, canEdit, canDelete } = useAuth();
  const [remainders, setRemainders] = useState([]);
  const [newRemainder, setNewRemainder] = useState({
    title: '',
    remarks: '',
    cycle: 'Monthly',
    start_date: format(new Date(), 'yyyy-MM-dd')
  });
  const [editingRemainder, setEditingRemainder] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [filterCycle, setFilterCycle] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const cycleOptions = ['One Time', 'Daily', 'Weekly', 'Monthly', 'Bi-Monthly', 'Quarterly'];

  useEffect(() => {
    loadRemainders();
  }, []);

  const loadRemainders = async () => {
    setIsLoading(true);
    try {
      const data = await remainderAPI.getAll();
      setRemainders(data);
    } catch (error) {
      toast.error('Failed to load remainders');
      console.error('Error loading remainders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRemainder = async () => {
    if (!newRemainder.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!newRemainder.start_date) {
      toast.error('Please select a start date');
      return;
    }

    try {
      const created = await remainderAPI.create(newRemainder);
      setRemainders([...remainders, created]);
      setNewRemainder({
        title: '',
        remarks: '',
        cycle: 'Monthly',
        start_date: format(new Date(), 'yyyy-MM-dd')
      });
      setIsAddDialogOpen(false);
      toast.success('Remainder added successfully');
    } catch (error) {
      toast.error('Failed to add remainder');
      console.error('Error adding remainder:', error);
    }
  };

  const handleEditRemainder = async () => {
    if (!editingRemainder.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const updated = await remainderAPI.update(editingRemainder.id, {
        title: editingRemainder.title,
        remarks: editingRemainder.remarks,
        cycle: editingRemainder.cycle,
        start_date: editingRemainder.start_date
      });
      setRemainders(remainders.map(r => r.id === editingRemainder.id ? updated : r));
      setIsEditDialogOpen(false);
      setEditingRemainder(null);
      toast.success('Remainder updated successfully');
    } catch (error) {
      toast.error('Failed to update remainder');
      console.error('Error updating remainder:', error);
    }
  };

  const handleDeleteRemainder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this remainder?')) return;

    try {
      await remainderAPI.delete(id);
      setRemainders(remainders.filter(r => r.id !== id));
      toast.success('Remainder deleted successfully');
    } catch (error) {
      toast.error('Failed to delete remainder');
      console.error('Error deleting remainder:', error);
    }
  };

  const handleMarkComplete = async (remainder) => {
    // Store the previous due date before completing
    const previousDueDate = remainder.next_due_date;
    try {
      const updated = await remainderAPI.complete(remainder.id);
      // Store the previous due date in the updated object for undo functionality
      updated._previousDueDate = previousDueDate;
      setRemainders(remainders.map(r => r.id === remainder.id ? updated : r));
      toast.success('Marked as completed! Next occurrence scheduled.');
    } catch (error) {
      toast.error('Failed to mark as completed');
      console.error('Error completing remainder:', error);
    }
  };

  const handleUndoComplete = async (remainder) => {
    // Calculate what the previous due date should be based on cycle
    const previousDueDate = remainder._previousDueDate || calculatePreviousDueDate(remainder.next_due_date, remainder.cycle);
    
    try {
      const updated = await remainderAPI.undoComplete(remainder.id, previousDueDate);
      setRemainders(remainders.map(r => r.id === remainder.id ? updated : r));
      toast.success('Marked as pending. Due date restored.');
    } catch (error) {
      toast.error('Failed to undo completion');
      console.error('Error undoing completion:', error);
    }
  };

  // Helper to calculate previous due date for undo
  const calculatePreviousDueDate = (currentDueDate, cycle) => {
    const current = parseISO(currentDueDate);
    const cycleDays = {
      'Daily': 1,
      'Weekly': 7,
      'Monthly': 30,
      'Bi-Monthly': 60,
      'Quarterly': 90
    };
    const days = cycleDays[cycle] || 30;
    const previous = new Date(current);
    previous.setDate(previous.getDate() - days);
    return format(previous, 'yyyy-MM-dd');
  };

  const getDueStatus = (remainder) => {
    // For completed one-time tasks
    if (remainder.status === 'Completed') return 'completed';
    
    const nextDueDate = remainder.next_due_date;
    if (!nextDueDate) return 'pending';
    const dueDate = parseISO(nextDueDate);
    if (isToday(dueDate)) return 'due-today';
    if (isPast(dueDate)) return 'overdue';
    return 'upcoming';
  };

  const getStatusBadge = (remainder) => {
    const dueStatus = getDueStatus(remainder);
    
    if (dueStatus === 'completed') {
      return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
    }
    if (dueStatus === 'overdue') {
      return <Badge variant="destructive" className="animate-pulse">Overdue</Badge>;
    }
    if (dueStatus === 'due-today') {
      return <Badge variant="destructive">Due Today</Badge>;
    }
    return <Badge variant="outline">Upcoming</Badge>;
  };

  const getCycleBadge = (cycle) => {
    const colors = {
      'One Time': 'bg-gray-100 text-gray-800',
      'Daily': 'bg-blue-100 text-blue-800',
      'Weekly': 'bg-green-100 text-green-800',
      'Monthly': 'bg-purple-100 text-purple-800',
      'Bi-Monthly': 'bg-orange-100 text-orange-800',
      'Quarterly': 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[cycle] || 'bg-gray-100 text-gray-800'}>{cycle}</Badge>;
  };

  const filteredRemainders = remainders.filter(rem => {
    const matchesCycle = filterCycle === 'all' || rem.cycle === filterCycle;
    const dueStatus = getDueStatus(rem);
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'pending' && (dueStatus === 'overdue' || dueStatus === 'due-today')) ||
      (filterStatus === 'upcoming' && dueStatus === 'upcoming') ||
      (filterStatus === 'completed' && dueStatus === 'completed');
    return matchesCycle && matchesStatus;
  });

  // Sort by due date - overdue first, then due today, then upcoming, then completed
  const sortedRemainders = [...filteredRemainders].sort((a, b) => {
    const aStatus = getDueStatus(a);
    const bStatus = getDueStatus(b);
    const statusOrder = { 'overdue': 0, 'due-today': 1, 'upcoming': 2, 'completed': 3 };
    
    if (statusOrder[aStatus] !== statusOrder[bStatus]) {
      return statusOrder[aStatus] - statusOrder[bStatus];
    }
    
    return new Date(a.next_due_date) - new Date(b.next_due_date);
  });

  const stats = {
    total: remainders.length,
    overdue: remainders.filter(r => getDueStatus(r) === 'overdue').length,
    dueToday: remainders.filter(r => getDueStatus(r) === 'due-today').length,
    upcoming: remainders.filter(r => getDueStatus(r) === 'upcoming').length,
    completed: remainders.filter(r => getDueStatus(r) === 'completed').length
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading remainders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10">
            <Clock className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Remainder</h1>
            <p className="text-muted-foreground mt-1">Maintenance schedule and recurring tasks</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-muted-foreground">Total Schedules</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
            <p className="text-sm text-red-600">Overdue</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">{stats.dueToday}</div>
            <p className="text-sm text-orange-600">Due Today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{stats.upcoming}</div>
            <p className="text-sm text-muted-foreground">Upcoming</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <p className="text-sm text-green-600">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex gap-2">
              <Select value={filterCycle} onValueChange={setFilterCycle}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Cycle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cycles</SelectItem>
                  {cycleOptions.map(cycle => (
                    <SelectItem key={cycle} value={cycle}>{cycle}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending/Overdue</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {canAdd('remainder') && (
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Add New Schedule</DialogTitle>
                    <DialogDescription>Create a new maintenance schedule or recurring task</DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={newRemainder.title}
                        onChange={(e) => setNewRemainder({ ...newRemainder, title: e.target.value })}
                        placeholder="e.g., Machine Maintenance, Oil Change"
                        data-testid="remainder-title-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="cycle">Repeat Cycle *</Label>
                      <Select
                        value={newRemainder.cycle}
                        onValueChange={(value) => setNewRemainder({ ...newRemainder, cycle: value })}
                      >
                        <SelectTrigger data-testid="remainder-cycle-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {cycleOptions.map(cycle => (
                            <SelectItem key={cycle} value={cycle}>{cycle}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={newRemainder.start_date}
                        onChange={(e) => setNewRemainder({ ...newRemainder, start_date: e.target.value })}
                        data-testid="remainder-start-date-input"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="remarks">Remarks</Label>
                      <Textarea
                        id="remarks"
                        value={newRemainder.remarks}
                        onChange={(e) => setNewRemainder({ ...newRemainder, remarks: e.target.value })}
                        placeholder="Additional notes or instructions"
                        data-testid="remainder-remarks-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddRemainder} data-testid="remainder-add-btn">Add Schedule</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sortedRemainders.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No schedules found</p>
              <p className="text-muted-foreground">Add your first maintenance schedule to get started</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Next Due</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead>Last Completed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRemainders.map((remainder) => {
                    const dueStatus = getDueStatus(remainder);
                    const isOverdueOrDue = dueStatus === 'overdue' || dueStatus === 'due-today';
                    const isCompleted = dueStatus === 'completed';
                    
                    return (
                      <TableRow 
                        key={remainder.id} 
                        className={isCompleted ? 'bg-green-50 hover:bg-green-100' : (isOverdueOrDue ? 'bg-red-50 hover:bg-red-100' : '')}
                        data-testid={`remainder-row-${remainder.id}`}
                      >
                        <TableCell className={`font-medium ${isCompleted ? 'text-green-700 line-through' : (isOverdueOrDue ? 'text-red-700' : '')}`}>
                          {remainder.title}
                        </TableCell>
                        <TableCell>{getCycleBadge(remainder.cycle)}</TableCell>
                        <TableCell>{remainder.start_date}</TableCell>
                        <TableCell className={isOverdueOrDue ? 'text-red-700 font-semibold' : ''}>
                          {remainder.next_due_date}
                        </TableCell>
                        <TableCell>{getStatusBadge(remainder)}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={remainder.remarks}>
                          {remainder.remarks || '-'}
                        </TableCell>
                        <TableCell>
                          {remainder.last_completed_at 
                            ? format(parseISO(remainder.last_completed_at), 'yyyy-MM-dd HH:mm')
                            : '-'
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {canEdit('remainder') && !isCompleted && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMarkComplete(remainder)}
                                title="Mark as Completed"
                                data-testid={`complete-btn-${remainder.id}`}
                              >
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {canEdit('remainder') && remainder.last_completed_at && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUndoComplete(remainder)}
                                title="Undo - Mark as Pending"
                                data-testid={`undo-btn-${remainder.id}`}
                              >
                                <Undo2 className="h-4 w-4 text-orange-600" />
                              </Button>
                            )}
                            {canEdit('remainder') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingRemainder(remainder);
                                  setIsEditDialogOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete('remainder') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRemainder(remainder.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Schedule</DialogTitle>
            <DialogDescription>Update schedule details</DialogDescription>
          </DialogHeader>
          {editingRemainder && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Title *</Label>
                <Input
                  value={editingRemainder.title}
                  onChange={(e) => setEditingRemainder({ ...editingRemainder, title: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Repeat Cycle</Label>
                <Select
                  value={editingRemainder.cycle}
                  onValueChange={(value) => setEditingRemainder({ ...editingRemainder, cycle: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cycleOptions.map(cycle => (
                      <SelectItem key={cycle} value={cycle}>{cycle}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editingRemainder.start_date || ''}
                  onChange={(e) => setEditingRemainder({ ...editingRemainder, start_date: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Remarks</Label>
                <Textarea
                  value={editingRemainder.remarks || ''}
                  onChange={(e) => setEditingRemainder({ ...editingRemainder, remarks: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditRemainder}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Remainder;
